import React from 'react'
import { InfoWindow, Marker } from '@react-google-maps/api'

import { Store } from '../StoreDetails/StoreDetails'
import {
  I18nText as StoreDetailsCardI18nText,
  StoreDetailsCard,
} from '../StoreDetailsCard/StoreDetailsCard'

import { MapLocationProps, MarkerProps, PinProps } from './MapTypes'
import { openGoogleDirectionMap } from './utils'

const PinDetailsCard = ({
  searchNo,
  position,
  store,
  storeDetailsHref,
  cardText,
}: {
  searchNo: number
  position: MapLocationProps
  store: Store
  storeDetailsHref?: (
    region: string,
    town: string,
    storeUrlTag: string,
  ) => string
  cardText: StoreDetailsCardI18nText
}) => {
  return (
    <StoreDetailsCard
      searchCard
      searchNo={searchNo}
      selected
      compactCard
      getDirectionsHref={openGoogleDirectionMap(
        `${position.lat},${position.lng}`,
      )}
      hideCloseIcon
      i18n={cardText}
      store={{
        id: store.id,
        displayName: store.displayName,
        relativeDistance: store.relativeDistance,
        openingTimes: store.openingTimes,
        address: `${
          store.address.addressLine1 ? `${store.address.addressLine1},` : ''
        } ${
          store.address.addressLine2 ? `${store.address.addressLine2},` : ''
        } ${
          store.address.addressLine3 ? `${store.address.addressLine3},` : ''
        } ${
          store.address.addressLine4 ? `${store.address.addressLine4},` : ''
        } ${store.address.postalCode ? store.address.postalCode : ''}`,
        storeFacility: store.storeFacility,
        status: store.status,
      }}
      storeDetailsHref={storeDetailsHref?.(
        store?.address?.addressLine5 || '',
        store?.address?.addressLine4 || '',
        store?.urlTag,
      )}
    />
  )
}

const getMarkerIcon = (
  selectedFillColor?: string,
  defaultFillColor?: string,
  customIcon?: string,
  isInfoWindowOpen?: boolean,
  isHovered?: boolean,
) => {
  const fallbackFillColor = isInfoWindowOpen
    ? '#ffffff'
    : isHovered
    ? '#ffffff'
    : '#000000'

  const fillColor = isInfoWindowOpen
    ? selectedFillColor
    : isHovered
    ? selectedFillColor
    : defaultFillColor

  return {
    ...(customIcon
      ? { url: customIcon }
      : {
          path:
            'M9.25 16.8636C9.25 8.09812 16.2906 1 24.9662 1C33.6418 1 40.6824 8.09812 40.6824 16.8636C40.6824 21.0837 39.366 25.4768 36.6819 30.7989C34.0648 35.9884 30.1652 42.0271 24.9662 49.616C19.7672 42.0271 15.8677 35.9871 13.2505 30.797C10.5664 25.4742 9.25 21.0811 9.25 16.8636Z',
        }),
    fillColor: fillColor || fallbackFillColor,
    fillOpacity: 1,
    labelOrigin: new google.maps.Point(25, 20),
    anchor: new google.maps.Point(20, 45),
    scale: customIcon ? 1 : 0.8,
  }
}

const MapMarker = ({
  position,
  title,
  counter,
  markerId,
  onMarkerClick,
  selectedMarkerId,
  defaultPinColor,
  selectedPinColor,
  hoverPinColor,
  mapInstance,
  showMarkerInfoWindow,
  customIcon,
  store,
  storeDetailsHref,
  cardText,
}: MarkerProps & {
  counter: number
  onMarkerClick(
    position: MapLocationProps,
    isInfoWindowOpen: boolean,
    markerId: number | null | undefined,
  ): void
  selectedMarkerId?: number | null
  defaultPinColor: PinProps | null
  hoverPinColor: PinProps | null
  selectedPinColor: PinProps | null
  mapInstance?: google.maps.Map | null
  storeDetailsHref?: (
    region: string,
    town: string,
    storeUrlTag: string,
  ) => string
  cardText: StoreDetailsCardI18nText
}): JSX.Element => {
  const [isHovered, setIsHovered] = React.useState<boolean>(false)
  const [isInfoWindowOpen, setIsInfoWindowOpen] = React.useState<boolean>(false)
  const [selectMarker, setSelectMarker] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!isInfoWindowOpen && mapInstance && selectMarker) {
      setSelectMarker(null)
      onMarkerClick(position, isInfoWindowOpen, markerId)
    }
  }, [isInfoWindowOpen, mapInstance])

  React.useEffect(() => {
    if (
      mapInstance &&
      !selectMarker &&
      Number(selectedMarkerId) === Number(markerId)
    ) {
      handleMarkerSelect()
    }
    if (
      typeof selectedMarkerId === 'number' &&
      Number(selectedMarkerId) !== Number(markerId)
    ) {
      setIsInfoWindowOpen(false)
    }
  }, [selectedMarkerId, mapInstance])

  const handleMarkerSelect = () => {
    if (markerId) {
      setSelectMarker(markerId)
      setIsInfoWindowOpen(!isInfoWindowOpen)
      onMarkerClick(position, !isInfoWindowOpen, markerId)
    }
  }

  return (
    <Marker
      onMouseOver={() => setIsHovered(true)}
      onFocus={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      onBlur={() => setIsHovered(false)}
      onClick={handleMarkerSelect}
      position={position}
      title={title}
      icon={getMarkerIcon(
        selectedPinColor?.fillColor,
        defaultPinColor?.fillColor,
        customIcon,
        isInfoWindowOpen,
        isHovered,
      )}
      {...(!customIcon && {
        label: {
          color: isInfoWindowOpen
            ? selectedPinColor?.textColor || '#000000'
            : isHovered
            ? hoverPinColor?.textColor || '#000000'
            : defaultPinColor?.textColor || '#ffffff',
          text: String(counter),
        },
      })}
    >
      {isInfoWindowOpen && showMarkerInfoWindow && (
        <InfoWindow
          onCloseClick={() => setIsInfoWindowOpen(false)}
          options={{ minWidth: 375 }}
        >
          <PinDetailsCard
            searchNo={counter}
            position={position}
            store={store}
            storeDetailsHref={storeDetailsHref}
            cardText={cardText}
          />
        </InfoWindow>
      )}
    </Marker>
  )
}

export default MapMarker
